import { Fragment, h } from "preact";
import { RoutableProps, route } from "preact-router";
import { useCallback, useEffect, useState } from "preact/hooks";
import { signup, fetchValidateInvitation } from "services/backend";
import { globalSystemConfig } from "state";
import { Button, Checkbox, Container, Errorbar, FormField, Spinner } from "uitoolkit";
import { useEditor, usePageTitle } from "hooks";
import { isValidEmail } from "services/utils";
import { type ValidateInvitationQuery } from "../../../graphql/types";
import { translate } from "services/translations";

const initialData = { email: "", username: "", password: "", terms: false };

export const SignupBox = (props: RoutableProps) => {
    usePageTitle(translate("signup_pagetitle"));

    const token = (props as any).matches?.token;
    const [isVerifyingToken, setIsVerifyingToken] = useState(!!token);
    const [showValidation, setShowValidation] = useState(false);
    const [invitationResult, setInvitationResult] = useState<ValidateInvitationQuery | null>(null);
    const [feedback, setFeedback] = useState("");
    const [waiting, setWaiting] = useState(false);
    const { data, setData, handleInputChange } = useEditor(initialData);

    useEffect(() => {
        if (token) {
            fetchValidateInvitation(token).then(result => {
                if (result.data) {
                    // if (result.data.invitation) {
                    //     result.data.invitation.name = "Ole Opfinder";
                    // }
                    setInvitationResult(result.data ?? null);
                    setData({
                        ...data,
                        username: result.data.invitation?.name ?? "",
                        email: result.data.invitation?.email ?? ""
                    });
                } else {

                }

                // todo: also handle if 

                setIsVerifyingToken(false);
            });
            setIsVerifyingToken(false);
        }
    }, [token, setData]);

    const onSignup = useCallback(async () => {
        setShowValidation(true);
        if (!data.terms || !isValidEmail(data.email) || !data.username || !data.password) return;
        setShowValidation(false);
        setWaiting(true);
        const res = await signup(data.username, data.email, data.password, token);
        if (res) {
            route("/chat");
            return;
        } else {
            // todo: handle if the result says the invitation has expired or been used
        }
        setFeedback(translate("signup_feedback_error"));
        setWaiting(false);
    }, [data.terms, data.email, data.username, data.password])

    const handleEnter = useCallback((e: KeyboardEvent) => {
        if (!waiting && e.key == "Enter")
            onSignup();
    }, [waiting, onSignup]);

    if (isVerifyingToken) {
        return (
            <Container class="component-form flex-column gap-16">
                <Spinner class="small" />
            </Container>
        );
    }

    if (!isVerifyingToken && !globalSystemConfig.value!.enableSignup && !(invitationResult && globalSystemConfig.value!.enableInvitedSignup)) {
        return (
            <Container class="component-form flex-column gap-16">
                <header>
                    <h1>{translate("signup_closed_headline")}</h1>
                    <h2>{translate("signup_closed_description")}</h2>
                </header>
            </Container>
        )
    }

    const hasInvalidInvitation = token && !isVerifyingToken && !invitationResult?.invitation?.valid

    if (!globalSystemConfig.value!.enableSignup && hasInvalidInvitation) {
        return (
            <Container class="component-form flex-column gap-16 center">
                <header>
                    <h1>{translate("signup_closed_headline")}</h1>
                    <h2>{translate("signup_closed_description")}</h2>
                    <h2>{translate("signup_error_invitation_expired")}</h2>
                </header>
            </Container>
        )
    }

    console.log(data);

    return (
        <Container class="component-form flex-column gap-16" onKeyUp={handleEnter}>
            <header>
                <h1>{translate("signup_headline")}</h1>
                <h2>{translate("signup_description")}</h2>
            </header>
            {
                feedback && <Errorbar class="orange">{feedback}</Errorbar>
            }
            {
                hasInvalidInvitation && <Errorbar class="red">{translate("signup_error_invitation_expired")}</Errorbar>
            }
            {
                invitationResult && !hasInvalidInvitation && <Fragment>
                    <FormField id="invitation" type="text" label="Invitation code" value={token} readonly disabled />
                </Fragment>
            }
            <FormField
                id="username"
                name="username"
                type="text"
                label={translate("signup_label_name")}
                placeholder={translate("signup_placeholder_name")}
                value={data.username}
                autoComplete=""
                onChange={handleInputChange}
                state={(!showValidation || !!invitationResult?.invitation?.name) ? undefined : (!data.username.trim() ? "invalid" : "valid")}
                disabled={waiting || !!invitationResult?.invitation?.name}
                readonly={!!invitationResult?.invitation?.name} />
            <FormField
                id="email"
                type="email"
                label={translate("signup_label_email")}
                placeholder={translate("signup_placeholder_email")}
                value={data.email}
                autoComplete=""
                state={!showValidation ? undefined : (!isValidEmail(data.email.trim()) ? "invalid" : "valid")}
                onChange={handleInputChange}
                disabled={waiting || !!invitationResult?.invitation?.email}
                readonly={!!invitationResult?.invitation?.email} />
            <FormField
                id="password"
                type="password"
                label={translate("signup_label_password")}
                placeholder=""
                value={data.password}
                autoComplete="new-password"
                state={!showValidation ? undefined : (data.password.trim().length < 6 ? "invalid" : "valid")}
                onChange={handleInputChange}
                disabled={waiting}
                showStrength />
            <Checkbox
                id="terms"
                label={translate("signup_label_agreeterms")}
                state={!showValidation ? undefined : (!data.terms ? "invalid" : "valid")}
                onChange={handleInputChange} />
            <Button
                variant="primary"
                size="large"
                class="wide"
                onClick={onSignup}
                disabled={waiting}>{translate("signup_button_signup")}</Button>
        </Container >
    );
};
